import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Filter, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { CategoryBadge, SeverityBadge, SourceBadge } from "@/components/Badges";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Database() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ categories: [], platforms: [], severities: [] });

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(searchParams.get("category") || "all");
  const [platform, setPlatform] = useState("all");
  const [severity, setSeverity] = useState("all");

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tutorials", {
        params: { search: search || undefined, category, platform, severity },
      });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [search, category, platform, severity]);

  useEffect(() => {
    const t = setTimeout(fetchRows, 250);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const triggerCls = "font-mono text-xs bg-[#0b111d] border-slate-800 text-slate-300 focus:ring-[#00ff66]";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-mono text-3xl font-extrabold">Exploit Database</h1>
          <p className="font-mono text-sm text-slate-500 mt-1">
            <span className="text-[#00ff66]">{rows.length}</span> entries indexed
          </p>
        </div>
      </div>

      {/* filters */}
      <div className="glass rounded-lg border border-slate-800 p-4 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-5 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="exploit-search-input"
              placeholder="search title, cve, tag..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 font-mono text-sm bg-[#0b111d] border-slate-800 text-slate-100"
            />
          </div>

          <div className="lg:col-span-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="category-filter-select" className={triggerCls}>
                <div className="flex items-center gap-2"><Filter size={13} /><SelectValue placeholder="Category" /></div>
              </SelectTrigger>
              <SelectContent className="bg-[#0b111d] border-slate-800 text-slate-200 font-mono text-xs">
                <SelectItem value="all">All categories</SelectItem>
                {meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="lg:col-span-2">
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="platform-filter-select" className={triggerCls}><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent className="bg-[#0b111d] border-slate-800 text-slate-200 font-mono text-xs">
                <SelectItem value="all">All platforms</SelectItem>
                {meta.platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="lg:col-span-2">
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="severity-filter-select" className={triggerCls}><SelectValue placeholder="Severity" /></SelectTrigger>
              <SelectContent className="bg-[#0b111d] border-slate-800 text-slate-200 font-mono text-xs">
                <SelectItem value="all">All severity</SelectItem>
                {meta.severities.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* table */}
      <div data-testid="exploit-database-table" className="rounded-lg border border-slate-800 bg-[#05080f]/80 overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-800 bg-[#0b111d] font-mono text-[10px] uppercase tracking-widest text-slate-500">
          <div className="col-span-1">Date</div>
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Platform</div>
          <div className="col-span-2">Severity</div>
        </div>

        {loading ? (
          <div className="p-10 text-center font-mono text-sm text-[#00ff66] cursor-blink">loading exploits</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center font-mono text-sm text-slate-500">// no entries match your filters</div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              data-testid={`exploit-row-${r.id}`}
              onClick={() => navigate(`/tutorial/${r.id}`)}
              className="group grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-slate-800/60 hover:bg-[#00ff66]/5 cursor-pointer transition-colors"
            >
              <div className="col-span-1 font-mono text-xs text-slate-500 hidden md:block">{r.date}</div>
              <div className="col-span-5">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-slate-100 group-hover:text-[#00ff66] transition-colors">{r.title}</span>
                  {r.ai_generated && <Sparkles size={12} className="text-[#a855f7]" />}
                  <SourceBadge source={r.source} />
                </div>
                {r.cve ? <span className="font-mono text-[10px] text-slate-600">{r.cve}</span> : null}
              </div>
              <div className="col-span-2 flex items-center"><CategoryBadge category={r.category} /></div>
              <div className="col-span-2 flex items-center font-mono text-xs text-slate-400">{r.platform}</div>
              <div className="col-span-2 flex items-center justify-between">
                <SeverityBadge severity={r.severity} />
                <ArrowRight size={14} className="text-slate-600 group-hover:text-[#00ff66] group-hover:translate-x-1 transition-all hidden md:block" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
